/**
 * CADÊNCIA DO RELATÓRIO DE PLAYBACK — de quanto em quanto tempo o player reporta sozinho.
 *
 * <p><b>Por que o servidor decide isto.</b> O relatório saía a cada 15s <i>e</i> a cada
 * troca de slot (~10s) de todo player, o tempo todo: ~12/min × 26 mil players = ~5.200
 * req/s. Só que os quatro consumidores do relatório não precisam disso — três são sob
 * demanda (painel do admin, API externa, dispersão por grupo) e o quarto, o supervisor de
 * sincronismo, roda a cada 30s e lê de um cache.</p>
 *
 * <p>Quem fica reportando é apenas quem está em grupo com sincronismo canônico (videowall),
 * porque é assim que o servidor percebe um player fora de fase. Isso <b>não</b> enfraquece
 * o sincronismo: a posição é calculada localmente por cada player a partir de (época,
 * durações, relógio do servidor) — o relatório é o cão de guarda, não o mecanismo.</p>
 *
 * <p>A decisão vem do servidor (e não cravada no build) porque 26 mil players não
 * atualizam juntos: config vale na hora, é reversível sem OTA e serve de kill-switch.</p>
 */

/** O que o player deve fazer, dado o que o servidor mandou. */
export interface ReportCadence {
  /** Intervalo do timer, em ms. `0` = sem timer (só sob demanda). */
  intervalMs: number;
  /** Reportar também a cada troca de slot? Só no modo legado. */
  reportOnSlotChange: boolean;
  /** Legado = o servidor não mandou cadência (versão antiga, ou kill-switch acionado). */
  legacy: boolean;
}

/** Heartbeat histórico, de antes de o servidor mandar na cadência. */
export const LEGACY_REPORT_INTERVAL_MS = 15_000;

/**
 * Traduz `playback_report_interval_seconds` do `/api/config/player/me`.
 *
 * | valor do servidor | resultado |
 * |---|---|
 * | ausente / não-numérico / negativo | legado: 15s **e** a cada troca de slot |
 * | `0` | sem timer, sem troca de slot — só responde ao comando SSE |
 * | `N > 0` | a cada N segundos, **sem** o disparo por troca de slot |
 *
 * <p>Valor ausente é tratado como legado de propósito: é o que servidor antigo devolve, e
 * também o que o kill-switch produz. Colapsar `undefined` num default numérico apagaria o
 * back-compat — um player novo contra servidor velho pararia de reportar e sumiria do
 * painel.</p>
 */
export function resolveReportCadence(
  serverSeconds: number | undefined | null,
): ReportCadence {
  const valido =
    typeof serverSeconds === 'number' &&
    Number.isFinite(serverSeconds) &&
    serverSeconds >= 0;

  if (!valido) {
    return {
      intervalMs: LEGACY_REPORT_INTERVAL_MS,
      reportOnSlotChange: true,
      legacy: true,
    };
  }

  return {
    // Math.floor: servidor mandando 2.5s não vira um timer de 2500ms por acidente —
    // a unidade do contrato é o segundo.
    intervalMs: Math.floor(serverSeconds) * 1000,
    reportOnSlotChange: false,
    legacy: false,
  };
}
